import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactElement } from 'react';

import { ApiError } from './api';
import type {
  AdminApi,
  AdminAuditEvent,
  AdminOpsSummary,
  ChecklistTemplateAdmin,
  ContractorProfile,
  EmailCaseRecipientPreview,
  EmailRecipientPolicy,
  InspectionCaseSummary,
  ReferenceDataset,
  ReferenceDatasetSummary,
  RubitimeBusinessConfig,
  UserSummary,
  VehicleProfile,
} from './admin-api';
import {
  actorTypeLabel,
  caseStatusLabel,
  emailScopeKindLabel,
  eventTypeLabel,
  formatYekaterinburg,
  KNOWN_AUDIT_EVENT_TYPES,
  problemMessage,
  problemTechnicalDetail,
  subjectTypeLabel,
} from './admin-presentation';
import {
  actorBusinessLabel,
  ANY_CASE_LABEL,
  ANY_CONTEXT_LABEL,
  ANY_CONTRACTOR_LABEL,
  ANY_EVENT_LABEL,
  ANY_TEMPLATE_LABEL,
  contractorBusinessLabel,
  emptyToNull,
  formatJson,
  formatRecipientList,
  inspectionCaseSelectorLabel,
  parseOptionalJsonObject,
  parseRecipientList,
  payloadTreeRows,
  policyListLabel,
  policySelectorSummary,
  redactPayloadForDisplay,
  REFERENCE_DATA_HAS_SCHEMA_METADATA,
  REFERENCE_SCOPE_HAS_APPROVED_SELECTOR,
  RUBITIME_JOURNAL_API_REACHABLE,
  RUBITIME_PROVIDER_CATALOGUE_EXISTS,
  subjectBusinessLabel,
  toIdMap,
} from './administration-presentation';
import {
  AdminCaseLink,
  CaseContractorCell,
  caseVehicleLabel,
  sortCasesByScheduledDesc,
} from './admin-ops-case-table';
import { PageHeader, StatusChip, statusToneClass, TechnicalDetails } from './admin-ui';
import { navigate } from './path-router';
import { templateBusinessLabel } from './template-admin-presentation';

function ProblemAlert(props: { readonly failure: unknown }): ReactElement {
  if (typeof props.failure === 'string') {
    return (
      <div role="alert" className="banner banner-closed">
        <p>{props.failure}</p>
      </div>
    );
  }
  const technical = problemTechnicalDetail(props.failure);
  return (
    <div role="alert" className="banner banner-closed">
      <p>{problemMessage(props.failure)}</p>
      {technical !== null ? (
        <TechnicalDetails>
          <p>{technical}</p>
        </TechnicalDetails>
      ) : null}
    </div>
  );
}

export function OpsDashboard(props: { readonly api: AdminApi }): ReactElement {
  const [summary, setSummary] = useState<AdminOpsSummary | null>(null);
  const [recent, setRecent] = useState<InspectionCaseSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    setError(null);
    void Promise.all([props.api.getOpsSummary(), props.api.listInspectionCases()])
      .then(([body, cases]) => {
        setSummary(body);
        const list = Array.isArray(cases) ? cases : [];
        setRecent(sortCasesByScheduledDesc(list).slice(0, 10));
      })
      .catch((failure: unknown) => {
        setSummary(null);
        setRecent(null);
        setError(failure);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [props.api]);

  return (
    <div className="pkg-c-ops">
      <PageHeader
        title="Операции"
        description="Текущие операционные показатели и осмотры из загруженной выборки."
      />
      {loading ? <p role="status">Загрузка…</p> : null}
      {error !== null ? (
        <div>
          <ProblemAlert failure={error} />
          <button type="button" onClick={reload}>
            Повторить
          </button>
        </div>
      ) : null}

      <div className="kpi-grid pkg-c-kpi-grid" role="status">
        <button
          type="button"
          className="kpi-card"
          onClick={() => navigate('/admin/inspections')}
        >
          <h2 className="kpi-label">Открытые осмотры</h2>
          <p className="kpi-value">{summary !== null ? summary.open_cases : '—'}</p>
          <p className="meta">Показатель сервера open_cases.</p>
          <span className="kpi-link">Перейти к осмотрам →</span>
        </button>
        <div className="kpi-card" aria-disabled="true">
          <h2 className="kpi-label">Ожидают предзаполнения</h2>
          <p className="kpi-value">—</p>
          <p className="meta">Нет достоверного показателя в API.</p>
        </div>
        <div className="kpi-card" aria-disabled="true">
          <h2 className="kpi-label">Готовы к осмотру</h2>
          <p className="kpi-value">—</p>
          <p className="meta">Нет достоверного показателя в API.</p>
        </div>
        <div className="kpi-card" aria-disabled="true">
          <h2 className="kpi-label">Требуют внимания</h2>
          <p className="kpi-value">—</p>
          <p className="meta">
            Нет утверждённого счётчика. Rubitime pending_exceptions не смешивается с этой карточкой.
          </p>
        </div>
      </div>

      <div className="pkg-c-ops-layout">
        <section className="pkg-c-panel" aria-labelledby="ops-recent-title">
          <div className="pkg-c-panel-head">
            <h2 id="ops-recent-title">Последние осмотры</h2>
            <AdminCaseLink href="/admin/inspections">Все осмотры →</AdminCaseLink>
          </div>
          <p className="meta">
            До 10 записей из загруженного списка осмотров, сортировка по времени записи
            (scheduled_start_at). Это не гарантированный «полный журнал системы».
          </p>
          {recent === null && error === null && !loading ? null : null}
          {recent !== null && recent.length === 0 ? (
            <p role="status" className="empty-state">
              Осмотры не найдены.
            </p>
          ) : null}
          {recent !== null && recent.length > 0 ? (
            <div className="table-wrap pkg-c-table-wrap">
              <table className="data-table pkg-c-case-table">
                <thead>
                  <tr>
                    <th scope="col">Подрядчик</th>
                    <th scope="col">Госномер</th>
                    <th scope="col">Номер осмотра</th>
                    <th scope="col">Статус</th>
                    <th scope="col">Запись</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <CaseContractorCell row={row} kind="inspections" />
                      </td>
                      <td>{caseVehicleLabel(row)}</td>
                      <td>
                        <span className="pkg-c-case-number">{row.case_number}</span>
                      </td>
                      <td>
                        <StatusChip
                          label={caseStatusLabel(row.status)}
                          tone={statusToneClass(row.status)}
                        />
                      </td>
                      <td>{formatYekaterinburg(row.booking.scheduled_start_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>

        <aside className="pkg-c-ops-aside">
          <section className="pkg-c-panel" aria-labelledby="ops-events-title">
            <h2 id="ops-events-title">События и задачи</h2>
            <p className="empty-state" role="status">
              Отдельная лента критических событий на главной не подключена к API. Используйте раздел
              «Аудит».
            </p>
            <AdminCaseLink href="/admin/audit">Открыть аудит →</AdminCaseLink>
          </section>
          <section className="pkg-c-panel" aria-labelledby="ops-quick-title">
            <h2 id="ops-quick-title">Быстрые ссылки</h2>
            <ul className="pkg-c-quick-links">
              <li>
                <AdminCaseLink href="/admin/inspections">Перейти к осмотрам</AdminCaseLink>
                <p className="meta">Список текущих осмотров.</p>
              </li>
              <li>
                <AdminCaseLink href="/admin/audit">Открыть аудит</AdminCaseLink>
                <p className="meta">Журнал системных событий.</p>
              </li>
              <li>
                <AdminCaseLink href="/admin/archive">Проверить архив</AdminCaseLink>
                <p className="meta">Поиск архивных осмотров.</p>
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

export function ReferenceDataAdmin(props: { readonly api: AdminApi }): ReactElement {
  const [items, setItems] = useState<readonly ReferenceDatasetSummary[]>([]);
  const [detail, setDetail] = useState<ReferenceDataset | null>(null);
  const [payloadText, setPayloadText] = useState('{}');
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const loadSeq = useRef(0);

  const reloadList = () => {
    setLoading(true);
    void props.api
      .listReferenceDatasets()
      .then((body) => {
        setItems(Array.isArray(body?.items) ? body.items : []);
        setError(null);
      })
      .catch((failure: unknown) => setError(failure))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadList();
  }, [props.api]);

  const open = (datasetKey: string) => {
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    setError(null);
    void props.api
      .getReferenceDataset(datasetKey)
      .then((row) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setDetail(row);
        setPayloadText(formatJson(row.payload));
        setError(null);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setError(failure);
      });
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    if (detail === null) {
      return;
    }
    const parsed = parseOptionalJsonObject(payloadText, 'Структура данных (JSON)');
    if (!parsed.ok || parsed.value === null) {
      setError(parsed.ok ? 'Структура данных (JSON) должен быть JSON-объектом.' : parsed.message);
      return;
    }
    const targetKey = detail.dataset_key;
    const expected = detail.version;
    const seq = loadSeq.current;
    void props.api
      .replaceReferenceDataset(targetKey, expected, parsed.value)
      .then((row) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setDetail(row);
        setPayloadText(formatJson(row.payload));
        setError(null);
        reloadList();
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setError(failure);
        if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
          void props.api.getReferenceDataset(targetKey).then((row) => {
            if (loadSeq.current !== seq) {
              return;
            }
            setDetail(row);
          });
        }
      });
  };

  const rows = detail === null ? [] : payloadTreeRows(detail.payload);

  return (
    <>
      <h2>Произвольные наборы данных</h2>
      <p className="meta">
        Экспертная полная замена JSON-справочников. Типизированные справочники — в соседних
        вкладках.
      </p>
      {loading ? <p role="status">Загрузка…</p> : null}
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {items.length === 0 && !loading ? <p>Нет администрируемых справочников.</p> : null}
      <ul>
        {items.map((row) => (
          <li key={row.dataset_key}>
            <button type="button" onClick={() => open(row.dataset_key)}>
              {row.display_name}
            </button>
          </li>
        ))}
      </ul>
      {detail !== null ? (
        <>
          <h2>{detail.display_name}</h2>
          <p className="meta">
            Просмотр не требует правки JSON.
            {REFERENCE_DATA_HAS_SCHEMA_METADATA
              ? null
              : ' Утверждённой схемы полей нет: изменение — экспертная полная замена.'}
          </p>
          <table>
            <thead>
              <tr>
                <th>Ключ</th>
                <th>Значение</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.path}>
                  <td>{row.path}</td>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <form onSubmit={save}>
            <p role="status" className="banner">
              Сохранение полностью заменяет набор данных. Текущая версия: {detail.version}.
            </p>
            <label>
              Структура данных (JSON)
              <textarea
                aria-label="Структура данных (JSON)"
                value={payloadText}
                onChange={(event) => setPayloadText(event.target.value)}
                rows={12}
              />
            </label>
            <div className="actions">
              <button type="submit">Заменить целиком</button>
              <button type="button" onClick={() => setPayloadText(formatJson(detail.payload))}>
                Вернуть серверное значение
              </button>
            </div>
          </form>
          <TechnicalDetails>
            <p>Ключ набора: {detail.dataset_key}</p>
            <p>
              версия {detail.version}; checksum {detail.payload_checksum}
            </p>
            <pre>{formatJson(detail.payload)}</pre>
          </TechnicalDetails>
        </>
      ) : null}
    </>
  );
}

function SelectorFields(props: {
  readonly disabled: boolean;
  readonly suffix: string;
  readonly templateId: string;
  readonly contractorId: string;
  readonly referenceScope: string;
  readonly templates: readonly ChecklistTemplateAdmin[];
  readonly contractors: readonly ContractorProfile[];
  readonly extraContractor: ContractorProfile | null;
  readonly onTemplateId: (value: string) => void;
  readonly onContractorId: (value: string) => void;
  readonly onReferenceScope: (value: string) => void;
}): ReactElement {
  const contractorOptions = props.contractors.slice();
  if (
    props.extraContractor !== null &&
    contractorOptions.every((row) => row.id !== props.extraContractor?.id)
  ) {
    contractorOptions.push(props.extraContractor);
  }
  return (
    <>
      <label>
        Шаблон
        <select
          aria-label={`Шаблон ${props.suffix}`}
          value={props.templateId}
          disabled={props.disabled}
          onChange={(event) => props.onTemplateId(event.target.value)}
        >
          <option value="">{ANY_TEMPLATE_LABEL}</option>
          {props.templates.map((row) => (
            <option key={row.id} value={row.id}>
              {templateBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Подрядчик
        <select
          aria-label={`Подрядчик ${props.suffix}`}
          value={props.contractorId}
          disabled={props.disabled}
          onChange={(event) => props.onContractorId(event.target.value)}
        >
          <option value="">{ANY_CONTRACTOR_LABEL}</option>
          {contractorOptions.map((row) => (
            <option key={row.id} value={row.id}>
              {contractorBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <details className="technical" open={props.referenceScope.trim().length > 0}>
        <summary>Контекст (JSON) — экспертная настройка</summary>
        <p className="meta">
          {REFERENCE_SCOPE_HAS_APPROVED_SELECTOR
            ? null
            : 'Утверждённого каталога/схемы reference_scope нет. Пустое значение — любой контекст.'}{' '}
          Не требуется для глобальной политики.
        </p>
        <label>
          Контекст (JSON)
          <textarea
            aria-label={`Контекст (JSON) ${props.suffix}`}
            value={props.referenceScope}
            disabled={props.disabled}
            onChange={(event) => props.onReferenceScope(event.target.value)}
            rows={6}
          />
        </label>
        {props.referenceScope.trim().length > 0 ? (
          <p className="meta">
            Предпросмотр контекста:{' '}
            {(() => {
              const parsed = parseOptionalJsonObject(props.referenceScope, 'Контекст (JSON)');
              return parsed.ok
                ? parsed.value === null
                  ? ANY_CONTEXT_LABEL
                  : formatJson(parsed.value)
                : parsed.message;
            })()}
          </p>
        ) : (
          <p className="meta">{ANY_CONTEXT_LABEL}</p>
        )}
      </details>
    </>
  );
}

type EmailPolicyScreen = 'list' | 'create' | 'edit';

export function EmailPolicyAdmin(props: { readonly api: AdminApi }): ReactElement {
  const [items, setItems] = useState<EmailRecipientPolicy[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplateAdmin[]>([]);
  const [contractors, setContractors] = useState<ContractorProfile[]>([]);
  const [extraContractor, setExtraContractor] = useState<ContractorProfile | null>(null);
  const [detail, setDetail] = useState<EmailRecipientPolicy | null>(null);
  const [screen, setScreen] = useState<EmailPolicyScreen>('list');
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [scopeKind, setScopeKind] = useState<'RULE' | 'GLOBAL'>('RULE');
  const [priority, setPriority] = useState('10');
  const [recipients, setRecipients] = useState('');
  const [createTemplateId, setCreateTemplateId] = useState('');
  const [createContractorId, setCreateContractorId] = useState('');
  const [createReferenceScope, setCreateReferenceScope] = useState('');
  const [editTemplateId, setEditTemplateId] = useState('');
  const [editContractorId, setEditContractorId] = useState('');
  const [editReferenceScope, setEditReferenceScope] = useState('');
  const [editRecipients, setEditRecipients] = useState('');
  const [caseInspectionId, setCaseInspectionId] = useState('');
  const [casePreview, setCasePreview] = useState<EmailCaseRecipientPreview | null>(null);
  const loadSeq = useRef(0);
  const casePreviewSeq = useRef(0);

  const templateMap = useMemo(() => toIdMap(templates), [templates]);
  const contractorMap = useMemo(() => {
    const map = toIdMap(contractors);
    if (extraContractor !== null) {
      map.set(extraContractor.id, extraContractor);
    }
    return map;
  }, [contractors, extraContractor]);

  const applyPolicy = (row: EmailRecipientPolicy) => {
    setDetail(row);
    setEditRecipients(formatRecipientList(row.recipients));
    if (row.scope_kind === 'GLOBAL') {
      setEditTemplateId('');
      setEditContractorId('');
      setEditReferenceScope('');
      return;
    }
    setEditTemplateId(row.template_id ?? '');
    setEditContractorId(row.contractor_organization_profile_id ?? '');
    setEditReferenceScope(row.reference_scope === null ? '' : formatJson(row.reference_scope));
  };

  const reload = () => {
    setLoading(true);
    void props.api
      .listEmailPolicies()
      .then((rows) => {
        setItems(rows);
        setError(null);
      })
      .catch((failure: unknown) => setError(failure))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    void props.api
      .listChecklistTemplates()
      .then((rows) => setTemplates(Array.isArray(rows) ? rows : []))
      .catch((failure: unknown) => setError(failure));
    void props.api
      .listContractorProfiles()
      .then((rows) => setContractors(Array.isArray(rows) ? rows : []))
      .catch((failure: unknown) => setError(failure));
  }, [props.api]);

  const openPolicy = (policyId: string) => {
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    setError(null);
    const listed = items.find((row) => row.id === policyId);
    if (listed !== undefined) {
      applyPolicy(listed);
      setScreen('edit');
    }
    void props.api
      .getEmailPolicy(policyId)
      .then((row) => {
        if (loadSeq.current !== seq) {
          return;
        }
        applyPolicy(row);
        setScreen('edit');
        const contractorId = row.contractor_organization_profile_id;
        if (contractorId !== null && !contractorMap.has(contractorId)) {
          void props.api.getContractorProfile(contractorId).then((profile) => {
            if (loadSeq.current !== seq) {
              return;
            }
            setExtraContractor(profile);
          });
        }
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setError(failure);
      });
  };

  const create = (event: FormEvent) => {
    event.preventDefault();
    const parsedRecipients = parseRecipientList(recipients);
    if (parsedRecipients.length === 0) {
      setError('Укажите хотя бы одного получателя.');
      return;
    }
    const global = scopeKind === 'GLOBAL';
    const parsedScope = global
      ? { ok: true as const, value: null }
      : parseOptionalJsonObject(createReferenceScope, 'Контекст (JSON)');
    if (!parsedScope.ok) {
      setError(parsedScope.message);
      return;
    }
    const seq = loadSeq.current;
    void props.api
      .createEmailPolicy({
        scope_kind: scopeKind,
        template_id: global ? null : emptyToNull(createTemplateId),
        reference_scope: parsedScope.value,
        contractor_organization_profile_id: global ? null : emptyToNull(createContractorId),
        priority: Number(priority),
        recipients: parsedRecipients,
      })
      .then(() => {
        if (loadSeq.current !== seq) {
          reload();
          return;
        }
        loadSeq.current += 1;
        setDetail(null);
        setScreen('list');
        setError(null);
        reload();
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setError(failure);
      });
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    if (detail === null) {
      return;
    }
    const parsedRecipients = parseRecipientList(editRecipients);
    if (parsedRecipients.length === 0) {
      setError('Укажите хотя бы одного получателя.');
      return;
    }
    const global = detail.scope_kind === 'GLOBAL';
    const parsedScope = global
      ? { ok: true as const, value: null }
      : parseOptionalJsonObject(editReferenceScope, 'Контекст (JSON)');
    if (!parsedScope.ok) {
      setError(parsedScope.message);
      return;
    }
    const targetId = detail.id;
    const expected = detail.version;
    const seq = loadSeq.current;
    void props.api
      .updateEmailPolicy(targetId, {
        expected_version: expected,
        status: detail.status,
        template_id: global ? null : emptyToNull(editTemplateId),
        reference_scope: parsedScope.value,
        contractor_organization_profile_id: global ? null : emptyToNull(editContractorId),
        priority: detail.priority,
        recipients: parsedRecipients,
      })
      .then((row) => {
        if (loadSeq.current !== seq) {
          return;
        }
        applyPolicy(row);
        reload();
        setError(null);
      })
      .catch((failure: unknown) => {
        if (loadSeq.current !== seq) {
          return;
        }
        setError(failure);
        if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
          void props.api.getEmailPolicy(targetId).then((row) => {
            if (loadSeq.current !== seq) {
              return;
            }
            applyPolicy(row);
          });
        }
      });
  };

  const previewCaseRecipients = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = caseInspectionId.trim();
    if (trimmed === '') {
      setError('Укажите идентификатор осмотра.');
      return;
    }
    const seq = casePreviewSeq.current + 1;
    casePreviewSeq.current = seq;
    void props.api
      .previewCaseEmailRecipients({ inspection_case_id: trimmed })
      .then((row) => {
        if (casePreviewSeq.current !== seq) {
          return;
        }
        setCasePreview(row);
        setError(null);
      })
      .catch((failure: unknown) => {
        if (casePreviewSeq.current !== seq) {
          return;
        }
        setCasePreview(null);
        setError(failure);
      });
  };

  const registryPolicies = useMemo(() => {
    const rules = items
      .filter((row) => row.scope_kind === 'RULE')
      .slice()
      .sort((left, right) => right.priority - left.priority);
    const globals = items.filter((row) => row.scope_kind === 'GLOBAL');
    return [...rules, ...globals];
  }, [items]);

  return (
    <>
      <PageHeader
        title="Рассылка результатов"
        description="Настройте, кому система отправляет итоговый PDF после завершения осмотра."
      />
      {loading ? <p role="status">Загрузка…</p> : null}
      {error !== null ? <ProblemAlert failure={error} /> : null}

      {screen === 'list' ? (
        <>
          <section className="info-card email-primary-recipient" aria-labelledby="email-primary-title">
            <h2 id="email-primary-title" className="section-title">
              Основной получатель результата
            </h2>
            <p>
              Для бронирований Native Booking система обязательно включает в рассылку подтверждённый
              email владельца бронирования. Этот адрес нельзя отключить в интерфейсе: он берётся из
              учётной записи после верификации при бронировании.
            </p>
            <p className="meta">
              Если подтверждённый email недоступен, основной получатель помечается как недоступный —
              дополнительные правила ниже не заменяют эту обязательную логику.
            </p>
          </section>

          <section className="info-card email-section" aria-labelledby="email-registry-title">
            <div className="section-header-row">
              <h2 id="email-registry-title" className="section-title">
                Дополнительные получатели / правила
              </h2>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  loadSeq.current += 1;
                  setError(null);
                  setScreen('create');
                }}
              >
                Создать правило
              </button>
            </div>
            <p className="meta">
              Сначала система ищет подходящее правило для шаблона и подрядчика. Если подходящего
              правила нет, используется резервное (глобальное) правило.
            </p>
            {registryPolicies.length === 0 && !loading ? (
              <p className="empty-state" role="status">
                Правила не созданы.
              </p>
            ) : (
              <ul className="policy-list">
                {registryPolicies.map((row) => (
                  <li key={row.id}>
                    <button type="button" onClick={() => openPolicy(row.id)}>
                      {policyListLabel(row, templateMap, contractorMap)}
                    </button>
                    <p className="meta">{policySelectorSummary(row, templateMap, contractorMap)}</p>
                    <p className="meta">Получатели: {formatRecipientList(row.recipients)}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <form
            className="info-card preview-panel form-section"
            onSubmit={previewCaseRecipients}
          >
            <h2>Проверка получателей</h2>
            <p className="meta">
              Укажите осмотр, для которого нужно показать фактический список получателей и статусы
              отправки (только просмотр).
            </p>
            <label>
              Осмотр
              <input
                aria-label="Идентификатор осмотра"
                value={caseInspectionId}
                onChange={(event) => {
                  casePreviewSeq.current += 1;
                  setCasePreview(null);
                  setCaseInspectionId(event.target.value);
                }}
              />
            </label>
            <div className="actions">
              <button type="submit">Проверить получателей</button>
            </div>
          </form>
          {casePreview !== null ? (
            <div className="info-card preview-result" role="status">
              <h3 className="section-title">
                Осмотр {casePreview.case_number}
              </h3>
              <p>
                <strong>Основной:</strong>{' '}
                {casePreview.primary.email ?? '—'} ({casePreview.primary.source_label})
              </p>
              {casePreview.primary.unavailable_reason !== null ? (
                <p className="meta">Причина недоступности: {casePreview.primary.unavailable_reason}</p>
              ) : null}
              <p>
                <strong>Дополнительные:</strong>{' '}
                {casePreview.additional_recipients.length === 0
                  ? '—'
                  : casePreview.additional_recipients
                      .map((row) => {
                        const kind =
                          row.scope_kind === null
                            ? ''
                            : ` · ${emailScopeKindLabel(row.scope_kind as 'RULE' | 'GLOBAL')}`;
                        return `${row.email}${kind}`;
                      })
                      .join('; ')}
              </p>
              <p>
                <strong>Итоговый список:</strong> {casePreview.resolved_recipients.join(', ') || '—'}
              </p>
              {casePreview.policy_error !== null ? (
                <p role="alert" className="banner banner-closed">
                  {casePreview.policy_error}
                </p>
              ) : null}
              {casePreview.deliveries.length > 0 ? (
                <>
                  <h4>Отправки</h4>
                  <ul>
                    {casePreview.deliveries.map((delivery) => (
                      <li key={delivery.id}>
                        {delivery.status} · попыток {delivery.attempt_count} · получателей{' '}
                        {delivery.recipient_count}
                        {delivery.sent_at !== null ? ` · ${delivery.sent_at}` : ''}
                        {delivery.last_error !== null ? ` · ${delivery.last_error}` : ''}
                        {delivery.reliability_note !== null ? (
                          <span className="meta">
                            {' '}
                            · Принято SMTP — не означает доставку во входящие
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="meta">Отправок по этому осмотру пока нет.</p>
              )}
              <TechnicalDetails>
                <p>Источник бронирования: {casePreview.booking_source}</p>
                <p>provenance: {casePreview.primary.provenance ?? 'null'}</p>
              </TechnicalDetails>
            </div>
          ) : null}
        </>
      ) : null}

      {screen === 'create' ? (
        <form className="info-card form-section" onSubmit={create}>
          <div className="actions">
            <button
              type="button"
              onClick={() => {
                loadSeq.current += 1;
                setScreen('list');
                setError(null);
              }}
            >
              Назад к списку
            </button>
          </div>
          <h2>Создать правило</h2>
          <label>
            Тип
            <select
              aria-label="Тип политики"
              value={scopeKind}
              onChange={(event) => {
                const next = event.target.value as 'RULE' | 'GLOBAL';
                setScopeKind(next);
                if (next === 'GLOBAL') {
                  setCreateTemplateId('');
                  setCreateContractorId('');
                  setCreateReferenceScope('');
                }
              }}
            >
              <option value="RULE">Правило</option>
              <option value="GLOBAL">Глобальная политика</option>
            </select>
          </label>
          <SelectorFields
            disabled={scopeKind === 'GLOBAL'}
            suffix="создания"
            templateId={createTemplateId}
            contractorId={createContractorId}
            referenceScope={createReferenceScope}
            templates={templates}
            contractors={contractors}
            extraContractor={null}
            onTemplateId={setCreateTemplateId}
            onContractorId={setCreateContractorId}
            onReferenceScope={setCreateReferenceScope}
          />
          <details className="technical">
            <summary>Дополнительные настройки</summary>
            <label>
              Приоритет
              <input
                aria-label="Приоритет"
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              />
            </label>
            <p className="meta">Большее число — выше приоритет.</p>
          </details>
          <label>
            Получатели
            <textarea
              aria-label="Получатели"
              value={recipients}
              onChange={(event) => setRecipients(event.target.value)}
              rows={3}
            />
          </label>
          <div className="actions">
            <button type="submit">Создать политику</button>
          </div>
        </form>
      ) : null}

      {screen === 'edit' && detail !== null ? (
        <form className="info-card form-section" onSubmit={save}>
          <div className="actions">
            <button
              type="button"
              onClick={() => {
                loadSeq.current += 1;
                setScreen('list');
                setDetail(null);
                setError(null);
              }}
            >
              Назад к списку
            </button>
          </div>
          <h2>Редактирование</h2>
          <p>
            {emailScopeKindLabel(detail.scope_kind)} ·{' '}
            {policySelectorSummary(detail, templateMap, contractorMap)}
          </p>
          <label>
            Статус
            <select
              aria-label="Статус политики"
              value={detail.status}
              onChange={(event) =>
                setDetail({ ...detail, status: event.target.value as 'ACTIVE' | 'INACTIVE' })
              }
            >
              <option value="ACTIVE">Активна</option>
              <option value="INACTIVE">Неактивна</option>
            </select>
          </label>
          <SelectorFields
            disabled={detail.scope_kind === 'GLOBAL'}
            suffix="политики"
            templateId={editTemplateId}
            contractorId={editContractorId}
            referenceScope={editReferenceScope}
            templates={templates}
            contractors={contractors}
            extraContractor={extraContractor}
            onTemplateId={setEditTemplateId}
            onContractorId={setEditContractorId}
            onReferenceScope={setEditReferenceScope}
          />
          <details className="technical">
            <summary>Дополнительные настройки</summary>
            <label>
              Приоритет
              <input
                aria-label="Приоритет политики"
                value={String(detail.priority)}
                onChange={(event) =>
                  setDetail({ ...detail, priority: Number(event.target.value) })
                }
              />
            </label>
            <p className="meta">Большее число — выше приоритет.</p>
          </details>
          <label>
            Получатели
            <textarea
              aria-label="Получатели политики"
              value={editRecipients}
              onChange={(event) => setEditRecipients(event.target.value)}
              rows={3}
            />
          </label>
          <div className="actions">
            <button type="submit">Сохранить политику</button>
          </div>
          <TechnicalDetails>
            <p>Идентификатор политики: {detail.id}</p>
            <p>версия {detail.version}</p>
            <p>template_id: {detail.template_id ?? 'null'}</p>
            <p>
              contractor_organization_profile_id:{' '}
              {detail.contractor_organization_profile_id ?? 'null'}
            </p>
          </TechnicalDetails>
        </form>
      ) : null}
    </>
  );
}

export function RubitimeConfigAdmin(props: { readonly api: AdminApi }): ReactElement {
  const [config, setConfig] = useState<RubitimeBusinessConfig | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    void props.api
      .getRubitimeConfig()
      .then((row) => {
        setConfig(row);
        setError(null);
      })
      .catch((failure: unknown) => setError(failure))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [props.api]);

  const save = (event: FormEvent) => {
    event.preventDefault();
    if (config === null) {
      return;
    }
    void props.api
      .replaceRubitimeConfig({
        expected_version: config.version,
        branch_id: config.branch_id,
        service_id: config.service_id,
        cooperator_id: config.cooperator_id,
      })
      .then((row) => {
        setConfig(row);
        setError(null);
      })
      .catch((failure: unknown) => {
        setError(failure);
        if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
          reload();
        }
      });
  };

  return (
    <>
      <PageHeader
        title="Rubitime"
        description="Здесь задаются идентификаторы Rubitime, которые связывают осмотры CheckTS с настройками записи в Rubitime."
      />
      {loading ? <p role="status">Загрузка…</p> : null}
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {RUBITIME_PROVIDER_CATALOGUE_EXISTS ? null : (
        <p className="meta" role="note">
          Каталог филиалов, услуг и контрагентов Rubitime не подключён. Идентификаторы вводятся
          вручную.
        </p>
      )}
      {config !== null ? (
        <form className="info-card form-section" onSubmit={save}>
          <h2>Идентификаторы Rubitime</h2>
          <label>
            Филиал Rubitime
            <input
              aria-label="Филиал Rubitime"
              value={config.branch_id}
              onChange={(event) => setConfig({ ...config, branch_id: event.target.value })}
            />
            <span className="meta">ID филиала</span>
          </label>
          <label>
            Услуга Rubitime
            <input
              aria-label="Услуга Rubitime"
              value={config.service_id}
              onChange={(event) => setConfig({ ...config, service_id: event.target.value })}
            />
            <span className="meta">ID услуги</span>
          </label>
          <label>
            Контрагент Rubitime
            <input
              aria-label="Контрагент Rubitime"
              value={config.cooperator_id}
              onChange={(event) => setConfig({ ...config, cooperator_id: event.target.value })}
            />
            <span className="meta">ID контрагента</span>
          </label>
          <div className="actions">
            <button type="submit">Сохранить конфигурацию</button>
          </div>
          <TechnicalDetails>
            <p>версия {config.version}</p>
          </TechnicalDetails>
        </form>
      ) : null}
      <section className="info-card" aria-labelledby="rubitime-outside-title">
        <h2 id="rubitime-outside-title" className="section-title">
          Что настраивается вне этого экрана
        </h2>
        <p className="meta">
          API key, rk и webhook secret настраиваются в инфраструктуре развёртывания и не вводятся
          здесь.
        </p>
      </section>
      {RUBITIME_JOURNAL_API_REACHABLE ? null : (
        <>
          <p className="meta" role="note">
            Журнал webhook станет доступен после появления HTTP-реализации в API. Сейчас запись
            журнала после материализации webhook недоступна с этого экрана.
          </p>
          <TechnicalDetails>
            <p>RTQ-001…016 остаются открытыми до HTTP-материализации журнала webhook.</p>
          </TechnicalDetails>
        </>
      )}
    </>
  );
}

export function AuditAdmin(props: { readonly api: AdminApi }): ReactElement {
  const [eventType, setEventType] = useState('');
  const [caseId, setCaseId] = useState('');
  const [items, setItems] = useState<AdminAuditEvent[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [cases, setCases] = useState<InspectionCaseSummary[]>([]);
  const [contractors, setContractors] = useState<ContractorProfile[]>([]);
  const [vehicles, setVehicles] = useState<VehicleProfile[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplateAdmin[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const catalogsLoaded = useRef(false);
  const searchSeq = useRef(0);

  useEffect(() => {
    if (catalogsLoaded.current) {
      return;
    }
    catalogsLoaded.current = true;
    void props.api
      .listUsers()
      .then((rows) => setUsers(Array.isArray(rows) ? rows : []))
      .catch((failure: unknown) => setError(failure));
    void props.api
      .listInspectionCases()
      .then((rows) => {
        const active = Array.isArray(rows) ? rows : [];
        void props.api
          .searchArchive()
          .then((archived) => {
            const extra = Array.isArray(archived) ? archived : [];
            const merged = new Map<string, InspectionCaseSummary>();
            for (const row of [...active, ...extra]) {
              merged.set(row.id, row);
            }
            setCases([...merged.values()]);
          })
          .catch(() => setCases(active));
      })
      .catch((failure: unknown) => setError(failure));
    void props.api
      .listContractorProfiles()
      .then((rows) => setContractors(Array.isArray(rows) ? rows : []))
      .catch(() => undefined);
    void props.api
      .listVehicleProfiles()
      .then((rows) => setVehicles(Array.isArray(rows) ? rows : []))
      .catch(() => undefined);
    void props.api
      .listChecklistTemplates()
      .then((rows) => setTemplates(Array.isArray(rows) ? rows : []))
      .catch(() => undefined);
  }, [props.api]);

  const userMap = useMemo(() => toIdMap(users), [users]);
  const caseMap = useMemo(() => toIdMap(cases), [cases]);
  const contractorMap = useMemo(() => toIdMap(contractors), [contractors]);
  const vehicleMap = useMemo(() => toIdMap(vehicles), [vehicles]);
  const templateMap = useMemo(() => toIdMap(templates), [templates]);

  const search = () => {
    const seq = searchSeq.current + 1;
    searchSeq.current = seq;
    setLoading(true);
    void props.api
      .searchAuditEvents({
        eventType: eventType.trim() === '' ? undefined : eventType.trim(),
        inspectionCaseId: caseId.trim() === '' ? undefined : caseId.trim(),
      })
      .then((rows) => {
        if (searchSeq.current !== seq) {
          return;
        }
        setItems(Array.isArray(rows) ? rows : []);
        setError(null);
      })
      .catch((failure: unknown) => {
        if (searchSeq.current !== seq) {
          return;
        }
        setError(failure);
      })
      .finally(() => {
        if (searchSeq.current === seq) {
          setLoading(false);
        }
      });
  };

  const resetFilters = () => {
    setEventType('');
    setCaseId('');
    setItems([]);
    setError(null);
  };

  return (
    <div className="pkg-c-audit">
      <PageHeader
        title="Аудит"
        description="Журнал событий по бизнес-сущностям. Технические идентификаторы скрыты по умолчанию."
      />
      {error !== null ? <ProblemAlert failure={error} /> : null}
      <form
        className="pkg-c-filter-bar"
        onSubmit={(event) => {
          event.preventDefault();
          search();
        }}
      >
        <div className="pkg-c-filter-fields">
          <label>
            Тип события
            <select
              aria-label="Тип события"
              value={eventType}
              onChange={(event) => setEventType(event.target.value)}
            >
              <option value="">{ANY_EVENT_LABEL}</option>
              {KNOWN_AUDIT_EVENT_TYPES.map((code) => (
                <option key={code} value={code}>
                  {eventTypeLabel(code)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Осмотр
            <select
              aria-label="Осмотр"
              value={caseId}
              onChange={(event) => setCaseId(event.target.value)}
            >
              <option value="">{ANY_CASE_LABEL}</option>
              {cases.map((row) => (
                <option key={row.id} value={row.id}>
                  {inspectionCaseSelectorLabel(row)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="meta pkg-c-filter-note">
          Доступны серверные фильтры: тип события и осмотр. Фильтры «Исполнитель» и «Период» в
          searchAuditEvents не поддержаны — элементы не показываются. Список осмотров ограничен
          текущей выдачей и архивным поиском; историческая полнота не гарантируется.
        </p>
        <div className="pkg-c-filter-actions">
          <button type="submit">
            Найти события
          </button>
          <button type="button" className="btn-ghost" disabled={loading} onClick={resetFilters}>
            Сбросить
          </button>
        </div>
      </form>
      {loading ? <p role="status">Загрузка…</p> : null}
      <section className="pkg-c-panel" aria-labelledby="audit-journal-title">
        <div className="pkg-c-panel-head">
          <h2 id="audit-journal-title">Журнал событий</h2>
          {items.length > 0 ? (
            <p className="meta" role="status">
              В ответе API: {items.length} (не общий архив всех событий системы)
            </p>
          ) : null}
        </div>
        {items.length === 0 && !loading ? (
          <p className="empty-state" role="status">
            События не найдены.
          </p>
        ) : null}
        {items.length > 0 ? (
          <div className="table-wrap pkg-c-table-wrap">
            <table className="data-table audit-table pkg-c-case-table">
              <thead>
                <tr>
                  <th scope="col">Дата и время</th>
                  <th scope="col">Событие</th>
                  <th scope="col">Осмотр</th>
                  <th scope="col">Исполнитель</th>
                  <th scope="col">Раздел</th>
                  <th scope="col">Действие</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const caseRow =
                    row.inspection_case_id !== null
                      ? caseMap.get(row.inspection_case_id)
                      : undefined;
                  return (
                    <tr key={row.id} className="audit-row">
                      <td>{formatYekaterinburg(row.occurred_at)}</td>
                      <td>{eventTypeLabel(row.event_type)}</td>
                      <td>
                        {caseRow !== undefined ? (
                          <>
                            <span className="pkg-c-case-number">{caseRow.case_number}</span>
                            <br />
                            <span className="meta">
                              {snapshotTextSafe(caseRow.contractor) ?? '—'}
                            </span>
                          </>
                        ) : row.inspection_case_id !== null ? (
                          <span className="meta">Осмотр без подписи в выборке</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{actorBusinessLabel(row, userMap)}</td>
                      <td>{subjectTypeLabel(row.subject_type)}</td>
                      <td>
                        <details className="pkg-c-audit-details">
                          <summary>Подробнее</summary>
                          <p>
                            {subjectBusinessLabel(row, {
                              contractors: contractorMap,
                              vehicles: vehicleMap,
                              templates: templateMap,
                              cases: caseMap,
                              users: userMap,
                            })}
                          </p>
                          <TechnicalDetails>
                            <p>event_type: {row.event_type}</p>
                            <p>
                              {actorTypeLabel(row.actor_type)} · actor_type: {row.actor_type}
                            </p>
                            <p>
                              {subjectTypeLabel(row.subject_type)} · subject_type: {row.subject_type}
                            </p>
                            <pre>{formatJson(redactPayloadForDisplay(row.payload))}</pre>
                          </TechnicalDetails>
                        </details>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function snapshotTextSafe(contractor: Record<string, unknown> | undefined): string | null {
  if (contractor === undefined) {
    return null;
  }
  const legal = contractor.legal_name;
  if (typeof legal === 'string' && legal.trim().length > 0) {
    return legal.trim();
  }
  const name = contractor.name;
  if (typeof name === 'string' && name.trim().length > 0) {
    return name.trim();
  }
  const display = contractor.display_name;
  if (typeof display === 'string' && display.trim().length > 0) {
    return display.trim();
  }
  return null;
}
